import { Fragment } from 'react';
import { styled } from '@mui/material/styles';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ReplyIcon from '@mui/icons-material/Reply';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { WA_BUTTON_TYPE, WA_COMPONENT_TYPE, WA_HEADER_FORMAT } from '@constants/campaignTemplates';
import { matchPlaceholders, substituteSamples } from './waTemplateUtils';

const Bubble = styled('div')({
    background: '#fff',
    borderRadius: '0 8px 8px 8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
    padding: 8,
    maxWidth: 300,
    width: '100%',
    fontFamily: 'Rubik, sans-serif',
});

const HeaderImage = styled('div')({
    height: 96,
    borderRadius: 6,
    background: '#E8ECEF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-tertiary)',
    marginBottom: 6,
    overflow: 'hidden',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
});

const ButtonRow = styled('div')({
    borderTop: '1px solid var(--divider)',
    marginTop: 8,
    padding: '10px 0 4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: '#1DAA61',
    fontSize: 14,
    fontWeight: 500,
    '& svg': {
        fontSize: 18,
    },
});

const VarChip = styled('span')({
    display: 'inline-block',
    background: '#E3F0FB',
    color: '#1976D2',
    borderRadius: 4,
    padding: '0 4px',
    fontSize: '0.85em',
    whiteSpace: 'nowrap',
});

const BUTTON_ICONS = {
    [WA_BUTTON_TYPE.QUICK_REPLY]: <ReplyIcon />,
    [WA_BUTTON_TYPE.URL]: <LaunchIcon />,
    [WA_BUTTON_TYPE.PHONE_NUMBER]: <PhoneOutlinedIcon />,
};

const renderWithChips = text => {
    const nodes = [];
    let cursor = 0;
    matchPlaceholders(text).forEach(match => {
        if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
        nodes.push(<VarChip key={`${match.index}-${match[1]}`}>{`{{${match[1]}}}`}</VarChip>);
        cursor = match.index + match[0].length;
    });
    if (cursor < text.length) nodes.push(text.slice(cursor));
    return nodes.map((node, index) => <Fragment key={index}>{node}</Fragment>);
};

/**
 * WhatsApp-style bubble rendering of a template's components. With `substitute`, sample
 * values replace variables (builder live preview); without it, variables render as chips
 * (library card / campaign picker).
 */
const BubblePreview = ({ components, headerMediaUrl, mappings, substitute = false }) => {
    const { t } = useTranslation();

    const header = (components || []).find(c => c.type === WA_COMPONENT_TYPE.HEADER);
    const body = (components || []).find(c => c.type === WA_COMPONENT_TYPE.BODY);
    const footer = (components || []).find(c => c.type === WA_COMPONENT_TYPE.FOOTER);
    const buttons = (components || []).find(c => c.type === WA_COMPONENT_TYPE.BUTTONS)?.buttons || [];

    const renderText = text => (substitute ? substituteSamples(text, mappings) : renderWithChips(text || ''));

    const isEmpty = !header && !body?.text && !footer?.text && buttons.length === 0;
    if (isEmpty) {
        return (
            <Bubble>
                <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                    {t('TemplateLibrary.waEmptyPreview')}
                </Typography>
            </Bubble>
        );
    }

    return (
        <Bubble>
            {header?.format === WA_HEADER_FORMAT.IMAGE && (
                <HeaderImage>
                    {headerMediaUrl ? <img src={headerMediaUrl} alt="" /> : <ImageOutlinedIcon />}
                </HeaderImage>
            )}
            {header?.format === WA_HEADER_FORMAT.TEXT && header.text && (
                <Typography variant="subtitle2" sx={{ marginBottom: '4px', wordBreak: 'break-word' }}>
                    {renderText(header.text)}
                </Typography>
            )}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {renderText(body?.text)}
            </Typography>
            {footer?.text && (
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}
                >
                    {footer.text}
                </Typography>
            )}
            {buttons.map((button, index) => (
                <ButtonRow key={index}>
                    {BUTTON_ICONS[button.type]}
                    <span>{button.text}</span>
                </ButtonRow>
            ))}
        </Bubble>
    );
};

export default BubblePreview;
